import React, { useEffect, useRef } from 'react';
import SectionHeading from '../UI/SectionHeading';
import Button from '../UI/Button';
import './WhySection.css';

interface TimelineStage {
  id: number;
  name: string;
  duration: string;
  kvadroHelps: boolean;
}

const WhySection: React.FC = () => {
  // Refs for animation elements
  const timelineRef = useRef<HTMLDivElement>(null);
  const solutionRef = useRef<HTMLDivElement>(null);
  
  // Development stages data
  const stages: TimelineStage[] = [
    {
      id: 1,
      name: 'Подготовка ТЗ на квартирографию',
      duration: '1 неделя',
      kvadroHelps: false
    },
    {
      id: 2,
      name: 'Разработка и согласование "пятен" квартир',
      duration: '2 месяца',
      kvadroHelps: true
    },
    {
      id: 3,
      name: 'Разработка и согласование концепции ОПР',
      duration: '1 месяц',
      kvadroHelps: true
    },
    {
      id: 4,
      name: 'Расстановка шахт и конструктивных элементов',
      duration: '2 месяца',
      kvadroHelps: false
    },
    {
      id: 5,
      name: 'Корректировка и повторное согласование ОПР',
      duration: '1 месяц',
      kvadroHelps: false
    }
  ];
  
  // Calculate timeline stats
  const totalDuration = stages.reduce((total, stage) => {
    const months = parseFloat(stage.duration.replace(/[^0-9.]/g, ''));
    return total + (stage.duration.includes('месяц') ? months : months / 4); // Convert weeks to months
  }, 0);
  
  const kvadroSavings = stages
    .filter(stage => stage.kvadroHelps)
    .reduce((total, stage) => {
      const months = parseFloat(stage.duration.replace(/[^0-9.]/g, ''));
      return total + (stage.duration.includes('месяц') ? months : months / 4);
    }, 0);
  
  // Animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (timelineRef.current) observer.observe(timelineRef.current);
    if (solutionRef.current) observer.observe(solutionRef.current);
    
    // Cleanup
    return () => {
      if (timelineRef.current) observer.unobserve(timelineRef.current);
      if (solutionRef.current) observer.unobserve(solutionRef.current);
    };
  }, []);
  
  return (
    <section id="why" className="why-section section">
      <div className="space-background">
        <div className="star-field"></div>
        <div className="planet"></div>
        <div className="orbit-line"></div>
        <div className="nebula"></div>
        <div className="shooting-star"></div>
      </div>
      
      <div className="container">
        {/* Problem Statement */}
        <div className="problem-section">
          <div className="section-intro">
            <SectionHeading 
              title="Проблема стандартного проектирования"
              subtitle="Разработка объемно-планировочных решений традиционно занимает до 6 месяцев"
            />
          </div>
          
          <div className="timeline-container" ref={timelineRef}>
            <div className="timeline-header">
              <h3 className="timeline-title">Этапы разработки объемно-планировочных решений</h3>
              <div className="timeline-stats">
                <div className="stats-item">
                  <div className="stats-value">6 мес</div>
                  <div className="stats-label">Стандартный срок</div>
                </div>
                <div className="stats-divider"></div>
                <div className="stats-item highlighted">
                  <div className="stats-value">{kvadroSavings} мес</div>
                  <div className="stats-label">KVADRO ускоряет</div>
                </div>
                <div className="stats-divider"></div>
                <div className="stats-item">
                  <div className="stats-value">≈ 130 млн ₽</div>
                  <div className="stats-label">Экономия</div>
                </div>
              </div>
            </div>
            
            <div className="timeline">
              {stages.map((stage, index) => (
                <div 
                  key={stage.id} 
                  className={`timeline-stage ${stage.kvadroHelps ? 'kvadro-accelerated' : ''}`}
                  style={{ '--animation-delay': `${index * 0.2}s` } as React.CSSProperties}
                >
                  <div className="stage-marker">
                    <div className="stage-number">{stage.id}</div>
                    <div className="stage-line"></div>
                  </div>
                  <div className="stage-content">
                    <div className="stage-info">
                      <h4 className="stage-name">{stage.name}</h4>
                      <div className="stage-duration">
                        <span className="duration-icon">⏱</span>
                        <span className="duration-text">{stage.duration}</span>
                      </div>
                    </div>
                    {stage.kvadroHelps && (
                      <div className="kvadro-indicator">
                        <div className="kvadro-badge">
                          <span className="kvadro-icon">⚡</span>
                          <span className="kvadro-text">Ускоряется с KVADRO</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="financial-impact">
            <div className="impact-visualization">
  <div className="impact-progress-container">
    <div className="impact-progress-label">Стандартный проект</div>
    <div className="impact-progress-bar">
      <div className="impact-progress-segment planning" style={{flex: '0 0 20%'}}>
        ОПР
      </div>
      <div className="impact-progress-segment development" style={{flex: '0 0 30%'}}>
        Подготовка
      </div>
      <div className="impact-progress-segment construction" style={{flex: '0 0 50%'}}>
        Строительство
      </div>
    </div>
  </div>
  
  <div className="impact-progress-container with-kvadro">
    <div className="impact-progress-label">С KVADRO</div>
    <div className="impact-progress-bar">
      <div className="impact-progress-segment planning reduced" style={{flex: '0 0 10%'}}>
        ОПР
      </div>
      <div className="impact-progress-segment development" style={{flex: '0 0 30%'}}>
      Подготовка
      </div>
      <div className="impact-progress-segment construction" style={{flex: '0 0 50%'}}>
        Строительство
      </div>
    </div>
  </div>
  
  <div className="time-savings">
    <div className="savings-value">-3 месяца</div>
    <div className="savings-description">
      10% от общего срока проекта строительства
    </div>
  </div>
</div>

              <div className="impact-details">
                <div className="impact-card">
                  <div className="impact-icon">💸</div>
                  <div className="impact-title">Экономия на процентах</div>
                  <div className="impact-value">≈ 130 млн ₽</div>
                  <div className="impact-description">
                    Сокращение выплат по кредитам за счет более раннего ввода объекта
                  </div>
                </div>
                
                <div className="impact-card">
                  <div className="impact-icon">👨‍💼</div>
                  <div className="impact-title">Эффективность команды</div>
                  <div className="impact-value">+50%</div>
                  <div className="impact-description">
                    Ключевые специалисты могут работать над несколькими проектами
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Solution Section */}
        <div className="solution-section" ref={solutionRef}>
          <div className="section-intro right-aligned">
            <SectionHeading 
              title="Как KVADRO решает проблему"
              subtitle="AI технологии превращают месяцы работы в минуты"
            />
          </div>
          
          <div className="solution-content">
            <div className="solution-benefits">
              <div className="benefit-card speed">
                <div className="benefit-card-content">
                  <div className="benefit-icon">⚡</div>
                  <h3 className="benefit-title">От месяцев к минутам</h3>
                  <p className="benefit-description">
                    AI алгоритмы KVADRO обрабатывают и анализируют данные в сотни раз быстрее человека
                  </p>
                </div>
                <div className="benefit-backdrop"></div>
              </div>
              
              <div className="benefit-card efficiency">
                <div className="benefit-card-content">
                  <div className="benefit-icon">🧠</div>
                  <h3 className="benefit-title">Интеллектуальная оптимизация</h3>
                  <p className="benefit-description">
                    Система учитывает все требования и нормы, предлагая оптимальные планировочные решения
                  </p>
                </div>
                <div className="benefit-backdrop"></div>
              </div>
              
              <div className="benefit-card adaptability">
                <div className="benefit-card-content">
                  <div className="benefit-icon">🔄</div>
                  <h3 className="benefit-title">Мгновенная регенерация</h3>
                  <p className="benefit-description">
                    Изменение параметров запускает мгновенный перерасчет с учетом всех зависимостей
                  </p>
                </div>
                <div className="benefit-backdrop"></div>
              </div>
              
              <div className="benefit-card standards">
                <div className="benefit-card-content">
                  <div className="benefit-icon">📋</div>
                  <h3 className="benefit-title">Соответствие нормам</h3>
                  <p className="benefit-description">
                    Все решения соответствуют российским стандартам проектирования и строительства
                  </p>
                </div>
                <div className="benefit-backdrop"></div>
              </div>
            </div>
          </div>
          
          <div className="solution-cta">
            <Button href="#demo" variant="gradient" size="large">
              Увидеть KVADRO в действии
            </Button>
          </div>
        </div>
      </div>
      
      {/* Decorative geometric elements */}
      <div className="geometric-elements">
        <div className="geo-element square"></div>
        <div className="geo-element circle"></div>
        <div className="geo-element triangle"></div>
        <div className="geo-element plus"></div>
      </div>
    </section>
  );
};

export default WhySection;